import { useCallback, useEffect, useState } from 'react';
import { Banner, SearchInput, Table, TCell, THead, TRow } from '@cou/admin-ui';
import { JurisdictionBadge, SegmentPill } from '../players/badges';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultPlayersClient,
  type AdminPlayer,
  type PlayersClient,
} from '../players/players-client';
import { formatDateParts } from '../contests/format-date';

/**
 * The Players list (API-21, admin-ui.md → Players): every game user making Entries, searchable by
 * account ref. This is the support entry point — Caesars support supplies the ref, ops pastes it
 * here — so search is the screen's primary control rather than an afterthought.
 *
 * The rows are pseudonymous by construction (decision 7): the `users` table holds no name or
 * email, so there is nothing to redact and `users:view` is the only gate.
 */

export interface PlayersScreenProps {
  /** Injected for tests; defaults to the real API-21 client. */
  readonly client?: PlayersClient;
}

/** How long to let an operator keep typing before asking the server (they paste a full ref). */
const SEARCH_DEBOUNCE_MS = 250;

export function PlayersScreen({ client = defaultPlayersClient }: PlayersScreenProps) {
  const { getToken } = useAdminAuth();
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<AdminPlayer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (query: string) => {
      const token = getToken();
      if (token === null) return;
      const result = await client.listPlayers(token, query ? { search: query } : {});
      if (result.ok) {
        setPlayers(result.users);
        setError(null);
      } else {
        // Never fall through to the empty state: "no such player" and "the lookup broke" are
        // opposite answers to give support, and only one of them is true.
        setError(result.message ?? 'The player lookup failed.');
        setPlayers(null);
      }
    },
    [client, getToken],
  );

  useEffect(() => {
    const timer = setTimeout(() => void load(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load, search]);

  return (
    <AdminShell
      title="Players"
      subtitle="Search by the account ref Caesars support supplies."
      actions={
        <SearchInput
          className="w-[300px] flex-shrink-0"
          pill={false}
          role="searchbox"
          aria-label="Search by account ref"
          placeholder="Account ref"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      }
    >
      {error !== null && (
        <Banner data-testid="players-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {players !== null && players.length === 0 && (
        <p data-testid="players-empty" className="mt-8 text-center text-sm text-ink-muted">
          No player matches that account ref.
        </p>
      )}

      {players !== null && players.length > 0 && (
        <Table className="mt-4" columns="minmax(0,1.4fr) 140px 90px 130px 130px 90px">
          <THead>
            <TCell>Account ref</TCell>
            <TCell>Segment</TCell>
            <TCell>State</TCell>
            <TCell>First seen</TCell>
            <TCell>Last seen</TCell>
            <TCell>Entries</TCell>
          </THead>
          {players.map((player) => (
            <TRow
              key={player.id}
              data-testid={`player-row-${player.id}`}
              href={`/players/${player.id}`}
            >
              <TCell variant="primary">
                <span className="font-mono text-[13px] text-accent">{player.account}</span>
              </TCell>
              <TCell>
                <SegmentPill>{segmentText(player)}</SegmentPill>
              </TCell>
              <TCell>
                <JurisdictionBadge>{player.state}</JurisdictionBadge>
              </TCell>
              <TCell variant="mono">{formatDateParts(player.createdAt).date}</TCell>
              <TCell variant="mono">{formatDateParts(player.lastSeenAt).date}</TCell>
              <TCell variant="num">{player.entryCount}</TCell>
            </TRow>
          ))}
        </Table>
      )}
    </AdminShell>
  );
}

/**
 * The segment as an operator should read it: its registry label, or — when the registry has no
 * entry for the ref (a retired segment, or one Caesars has not told us about) — the raw ref, which
 * support can still quote back. Null (the pill renders an em-dash) only when the player carried no
 * `seg` claim at all.
 */
function segmentText(player: AdminPlayer): string | null {
  return player.segmentLabel ?? player.segment ?? null;
}
